import React, { useState } from 'react';
import { Mention } from 'primereact/mention';
import { userService } from '@/services/user.service';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string, mentionedUserIds: string[]) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  rows?: number;
}

interface UserSuggestion {
  id: string;
  username: string;
  fullName: string;
  profilePicture?: string;
}

const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  placeholder = 'Write something...',
  disabled = false,
  rows = 4,
}) => {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search function for PrimeReact Mention
  const onSearch = async (event: { query: string }) => {
    const query = event.query.trim();
    console.log('🔍 Mention search triggered with query:', query);
    
    setIsSearching(true);
    
    try {
      const results = await userService.searchUsers(query);
      console.log('✅ Search results:', results);
      
      // Handle both array and {users: []} response formats
      const userArray = Array.isArray(results) ? results : (results as any).users || [];
      
      const formattedResults: UserSuggestion[] = userArray.map((user: any) => ({
        id: user.id,
        username: user.username || '',
        fullName: user.fullName,
        profilePicture: user.profilePicture,
      }));
      
      setSuggestions(formattedResults);
    } catch (error) {
      console.error('❌ Error searching users:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const onSelect = (e: any) => {
    console.log('👤 User selected:', e.suggestion);
    
    // Ignore selection if it's the loading state
    if (e.suggestion === 'loading' || !e.suggestion || !e.target) {
      return;
    }
    
    const user = e.suggestion as UserSuggestion;
    
    // Get current value and replace the plain @username with markup
    const currentValue = e.target.value || value;
    console.log('📝 Current value before conversion:', currentValue);
    
    // PrimeReact adds the username, we need to convert it to markup format
    // The value at this point should have @username in it
    const updatedValue = currentValue.replace(
      new RegExp(`@${user.username}(?!\\])`, 'g'),
      `@[${user.username}](${user.id})`
    );
    
    console.log('✅ Converted to markup:', updatedValue);
    
    // Extract mentioned user IDs
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    const mentionedUserIds: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(updatedValue)) !== null) {
      mentionedUserIds.push(match[2]);
    }
    
    console.log('📊 Extracted user IDs:', mentionedUserIds);
    
    // Update the parent component with the markup format
    onChange(updatedValue, Array.from(new Set(mentionedUserIds)));
  };

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    console.log('📝 onChange triggered:', newValue);
    
    // Extract mentioned user IDs from existing markup
    const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
    const mentionedUserIds: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(newValue)) !== null) {
      mentionedUserIds.push(match[2]);
    }
    
    onChange(newValue, Array.from(new Set(mentionedUserIds)));
  };

  const itemTemplate = (suggestion: UserSuggestion | string) => {
    // Handle loading state
    if (suggestion === 'loading') {
      return (
        <div className="flex items-center justify-center px-3 py-4" style={{ pointerEvents: 'none' }}>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
        </div>
      );
    }

    const user = suggestion as UserSuggestion;
    return (
      <div className="flex items-center gap-3 px-3 py-2.5">
        {user.profilePicture ? (
          <img
            src={user.profilePicture}
            alt={user.fullName}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold text-xs">
              {user.fullName.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {user.username}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {user.fullName}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="mention-textarea-wrapper">
      <Mention
        value={value}
        onChange={handleChange}
        suggestions={isSearching ? ['loading' as any] : suggestions}
        onSearch={onSearch}
        onSelect={onSelect}
        field="username"
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        itemTemplate={itemTemplate}
        inputClassName="mention-input"
        panelClassName="mention-panel"
        trigger="@"
      />

      <style>{`
        .mention-textarea-wrapper {
          width: 100%;
        }

        .mention-textarea-wrapper .p-mention {
          width: 100%;
          display: block;
        }

        .mention-textarea-wrapper .mention-input {
          width: 100% !important;
          padding: 16px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          outline: none;
          font-size: 16px;
          line-height: 1.5;
          font-family: inherit;
          color: #111827;
          transition: border-color 0.2s;
          resize: vertical;
          box-sizing: border-box;
        }

        .mention-textarea-wrapper .mention-input:focus {
          border-color: #111827;
          box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
        }

        .mention-textarea-wrapper .mention-input:disabled {
          background-color: #f9fafb;
          cursor: not-allowed;
        }

        .mention-textarea-wrapper {
          position: relative;
        }

        .mention-textarea-wrapper .p-mention-panel {
          background: white;
          border: 1px solid #dbdbdb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          margin-top: 4px;
          max-height: 240px;
          overflow-y: auto;
          z-index: 9999;
          position: absolute !important;
        }

        /* Mobile: Full width */
        @media (max-width: 639px) {
          .mention-textarea-wrapper .p-mention-panel {
            width: 100vw !important;
            left: 50% !important;
            margin-left: -50vw !important;
          }
        }

        /* Desktop: Normal width */
        @media (min-width: 640px) {
          .mention-textarea-wrapper .p-mention-panel {
            width: 100% !important;
            left: 0 !important;
            margin-left: 0 !important;
          }
        }

        .mention-textarea-wrapper .p-mention-item {
          padding: 0;
          border-bottom: 1px solid #efefef;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .mention-textarea-wrapper .p-mention-item:hover,
        .mention-textarea-wrapper .p-mention-item.p-highlight {
          background-color: #fafafa;
        }

        .mention-textarea-wrapper .p-mention-item:last-child {
          border-bottom: none;
        }

        /* Style mentions in the text */
        .mention-textarea-wrapper .p-mention {
          color: #0369a1;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default MentionTextarea;
